import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import React, { CSSProperties, createRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import './validation-input.css';
import { useDebounce, useTranslation } from '../../hooks';
import { ValueEditor } from 'react-querybuilder';


export interface ValidationDropDownItem {
    name: string;
    value: string | number;
}

export interface ValidationRefItem {
    name: string;
    ref: React.RefObject<ValidationInputRef>
}

export interface EditorValidContainer {
    [key: string]: boolean;
}

/**
 * Determines whether every tracked editor entry is currently valid.
 * @param validationObj - Map of editor identifiers to boolean validation flags.
 * @returns True when all entries are marked valid, otherwise false.
 */
export const isEditorValid = (validationObj: EditorValidContainer) => {

    for (const [key, value] of Object.entries(validationObj)) {
        if (!value) {
            return false;
        }
    }
    return true;

}

export interface ValidationInputRef {
    validate: () => boolean;
}

export interface ValidationInputEvent {
    value: any;
    originalEvent: React.SyntheticEvent;
}

export interface ValidationInputProps {
    id?: string;
    caption?: string;
    value?: string | number | string[];
    onChange?: (e: ValidationInputEvent) => void;
    onBlur?: (e: ValidationInputEvent) => void;
    type?: string;
    keyName?: string;
    required?: boolean;
    errorMessage?: string;
    readOnly?: boolean;
    options?: ValidationDropDownItem[] | any[];
    optionLabel?: string;
    multiple?: boolean;
    optionValue?: string;
    disabled?: boolean;
    className?: string;
    editorClassName?: string;
    showButton?: boolean;
    buttonIcon?: string;
    buttonIconColor?: string;
    buttonToolTip?: string;
    placeholder?: string;
    buttonTitle?: string;
    style?: CSSProperties;
    containerStyle?: CSSProperties;
    onClick?: (e: any) => void;
    labelStyle?: React.CSSProperties;
    itemTemplate?: (suggestion: any, index: number) => React.ReactNode;
    selectedTemplate?: (suggestion: any) => React.ReactNode;
    searchOptions?: (query: string, data: any[]) => Promise<any[]>;
    onEnter?: (e: any) => void;
    onValidationChanged?: (valid: boolean, id?: string) => void;
    onTranslate?: (value: string) => string;
    validationBounce?: number;
}

/**
 * Flexible input component that supports validation feedback, dropdown modes, and custom translations.
 * @param props - Configuration describing input type, validation requirements, and render options.
 * @param ref - Ref exposing a validate method for parent components.
 * @returns A validation-aware input or dropdown renderer with optional action button.
 */
export const ValidationInput = React.forwardRef((props: ValidationInputProps, ref) => {

    const translate = useTranslation();
    const [_value, setValue] = useState<string | number | undefined | string[]>(props.value)
    const [_autoCompleteValue, setAutoCompleteValue] = useState<any>();
    const [_className, setClassName] = useState<string>();
    const _editorHelpRef = createRef<HTMLElement>();
    const _dropdownHelpRef = createRef<HTMLElement>();
    const [_filteredOptions, setFilteredOptions] = useState<any[]>([])
    const [_items, setItems] = useState<any[]>([])
    const [_errorMessage, setErrorMessage] = useState<string>(props.errorMessage ?? '')
    const [_showPassword, setShowPassword] = useState<boolean>(false);
    const debounce = useDebounce(_value, props.validationBounce ?? 500);
    const [_hasChanged, setHasChanged] = useState<boolean>(false);
    const [_required, setRequired] = useState<boolean>(props.required ?? false);

    /**
     * Resolves translation text via external handler or default hook.
     * @param value - Translation key to resolve.
     * @returns Resolved localized string.
     */
    const internalTranslate = (value: string) => {
        if (props.onTranslate) {
            return props.onTranslate(value)
        }
        return translate(value);
    }

    useEffect(() => {
        if (props.options) {
            setFilteredOptions(props.options);

            setItems(props.options);
        }
        else {
            setItems([]);
            setFilteredOptions([])
        }
    }, [props.options]);

    useEffect(() => {
        validation();
        dropdownValidation(_value);
    }, [props.errorMessage, _required, _value])

    /**
     * Surfaces validation to parent refs based on the current input mode.
     */
    useImperativeHandle(ref, () => ({
        validate() {
            if (props.type === 'dropdown')
                return dropdownValidation(_value)
            else
                return validation();
        }
    }));



    useEffect(() => {
        if (_editorHelpRef.current) {
            if (_errorMessage) {
                setClassName('p-invalid');
                _editorHelpRef.current.style.display = 'block';
                _editorHelpRef.current.innerHTML = _errorMessage;
            } else {
                setClassName('');
                _editorHelpRef.current.style.display = 'none';
            }
        }

        if (_dropdownHelpRef.current) {
            if (_errorMessage) {
                setClassName('p-invalid');
                _dropdownHelpRef.current.style.display = 'block';
                _dropdownHelpRef.current.innerHTML = _errorMessage;
            } else {
                setClassName('');
                _dropdownHelpRef.current.style.display = 'none';
            }
        }

    }, [_errorMessage, _editorHelpRef, _dropdownHelpRef])

    useEffect(() => {

        if (Array.isArray(_value)) {
            let item = _items.filter(x => _value.indexOf(x[props.optionValue || 'id']) !== -1)
            setAutoCompleteValue(item);
        } else {
            let item = _items.find(x => x[props.optionValue || 'id'] === _value)
            setAutoCompleteValue(item);
        }

    }, [_items, _value, props.optionValue])

    useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    useEffect(() => {
        if (_required !== (props.required ?? false)) {
            setHasChanged(true);
        }
        setRequired(props.required ?? false);

    }, [props.required])

    /**
     * Performs validation on text input values and emits state updates.
     * @returns True if the current value satisfies validation rules.
     */
    const validation = useCallback(() => {
        let requiredText = internalTranslate("REQUIRED");

        if (!_hasChanged) {
            return true;
        }

        const clearValidation = () => {
            let message = props.errorMessage ?? ''
            if (message.length > 0 && message !== requiredText) {
                setErrorMessage(message);
                return false;
            }
            setErrorMessage("");
            return true;

        }

        const showRequired = () => {
            setErrorMessage(requiredText);
        }

        let isValid = true;

        if (_value === undefined && _required) {
            showRequired();
            isValid = false;
        }
        else {
            switch (typeof _value) {

                case 'string':

                    if (_value?.length > 0) {
                        let valid = clearValidation()
                        if (!valid) {
                            isValid = false;
                            break;
                        }

                    }
                    else {
                        if (_required) {
                            showRequired();
                            isValid = false;
                        } else {
                            clearValidation();
                        }
                    }
                    break;

                case 'number':
                    if (!isNaN(_value)) {
                        clearValidation();
                    } else if (_required) {
                        showRequired();
                        isValid = false;
                    }
                    break;

            }
        }
        if (props.onValidationChanged) {
            props.onValidationChanged(isValid, props.id);
        }
        return isValid;

    }, [_value, props.errorMessage, _hasChanged, _required]);

    useEffect(() => {
        validation();

    }, [debounce])

    /**
     * Displays the required indicator when the caption is present.
     */
    const renderRequired = () => {
        const show = _required && props.caption ? true : false
        return show ? <span className="fas fa-star required-star"  ></span> : ''
    }

    /**
     * Renders the text input variant including optional button and password toggle.
     * @returns JSX structure for the text input flavor.
     */
    const renderInputBox = () => {
        const handleChange = (e: any) => {

            setValue(e.target.value);
            setHasChanged(true);
            if (props.onChange) {
                props.onChange({
                    value: e.target.value,
                    originalEvent: e
                })
            }



        }

        const handleKeyDown = (e: any) => {
            if (e.key === "Enter") {
                validation();
                if (props.onEnter) {
                    props.onEnter({
                        value: e.target.value
                    })
                }
            }
        }

        const handleOnBlur = (e: any) => {
            // validation();
            if (props.onBlur)
                props.onBlur({
                    value: e.target.value,
                    originalEvent: e
                })
        }

        const showPasswordIcon = _showPassword ? "pi pi-eye" : "pi pi-eye-slash"
        const showIcon = props.type === 'password';
        const inputType = _showPassword ? "text" : props.type;

        return (
            <div className={props.caption ? 'field' : ''} key={props.keyName} style={{ width: '100%', ...props.style }}>
                {renderRequired()}
                {props.caption ? <><label style={props.labelStyle} className="item-authors">{internalTranslate(props.caption)}</label></> : ''}
                {props.showButton ?
                    <Button onClick={props.onClick} title={props.buttonTitle}
                        icon={props.buttonIcon} style={{ float: "right", height: 20, color: props.buttonIconColor ?? 'black' }} className="p-button-secondary p-button-text" aria-label={props.buttonToolTip} />
                    : <></>
                }

                <div className={showIcon ? "p-input-icon-right" : ''} style={{ width: '100%' }}>
                    {showIcon ? <i className={showPasswordIcon} onClick={() => setShowPassword(!_showPassword)} /> : <></>}
                    <InputText style={{ width: '100%' }} type={inputType} disabled={props.disabled} onBlur={handleOnBlur} placeholder={props.placeholder}
                        className={`${_className ?? ''} ${props.editorClassName ?? ''}`} readOnly={props.readOnly} id={props.id}
                        value={_value as string} aria-describedby="message-help" onChange={handleChange} onKeyDown={handleKeyDown} />

                </div>

                <small ref={_editorHelpRef} id={`${props.id}-help`} style={{ display: 'none', marginLeft: 2 }} className="p-error p-d-block"></small>
            </div>
        )
    }

    /**
     * Validates dropdown selections ensuring required values are populated.
     * @param value - Current dropdown selection value(s).
     * @returns True if the selection passes validation.
     */
    const dropdownValidation = (item: any) => {
        if (_dropdownHelpRef.current) {

            if (props.errorMessage) {
                setErrorMessage(props.errorMessage);
                return false;
            } else {
                setErrorMessage("");
            }

            if (_required) {
                let isValid = false
                if (Array.isArray(item))
                    isValid = item.length > 0;
                if (typeof item === 'object') {
                    let value: any = undefined;
                    value = item[props.optionValue || 'id']
                    isValid = value !== undefined && value !== null && value !== '';
                }
                else
                    isValid = item !== undefined && item !== null && item !== '';

                if (!isValid && _required === true) {
                    setClassName('p-invalid');
                    setErrorMessage(internalTranslate("REQUIRED"));
                    // _dropdownHelpRef.current.style.display = 'block';
                    // _dropdownHelpRef.current.innerHTML = internalTranslate("REQUIRED");
                } else {
                    setClassName('');
                    setErrorMessage("");
                }
                if (props.onValidationChanged) {
                    props.onValidationChanged(isValid, props.id);
                }
                return isValid;
            } else {
                if (props.onValidationChanged) {
                    props.onValidationChanged(true, props.id);
                }
                return true;
            }

        }
        return false;
    }

    /**
     * Renders the autocomplete dropdown input variant.
     * @returns JSX structure for the dropdown flavor.
     */
    const renderDropdown = () => {

        const handleAutoBlur = (e: any) => {
            if (props.onBlur) {
                props.onBlur({ value: undefined, originalEvent: e })
            }
        }

        /**
         * Updates selection state and propagates change events for dropdown values.
         * @param e - PrimeReact autocomplete change event.
         */
        const handleDropChanged = (e: any) => {
            if (props.readOnly === true || props.disabled) {
                return;
            }
            setAutoCompleteValue(e.value);
            let originalEvent = e;
            if (props.onChange) {
                let value: any = undefined;
                if (Array.isArray(e.value)) {
                    value = e.value.map((x: any) => x[props.optionValue || 'id'])
                    props.onChange({ value: value, originalEvent: originalEvent });
                } else if (typeof e.value === 'object') {
                    value = e.value[props.optionValue || 'id']
                    props.onChange({ value: value, originalEvent: originalEvent });
                } else
                    if (typeof e.value === 'object') {
                        value = e.value[props.optionValue || 'id']

                        props.onChange({ value: value, originalEvent: originalEvent });
                    } else {
                        props.onChange({ value: undefined, originalEvent: originalEvent })
                    }

            }
            if (_dropdownHelpRef.current) {
                setClassName('');
                _dropdownHelpRef.current.style.display = "none";
                _dropdownHelpRef.current.innerHTML = '';
            }
            dropdownValidation(e.value);

        }

        /**
         * Filters dropdown options locally or via async provider based on the search query.
         * @param event - PrimeReact autocomplete search event.
         */
        const searchOptions = async (event: AutoCompleteCompleteEvent) => {
            let filteredOptions: any[] = [];
            if (props.searchOptions) {
                filteredOptions = await props.searchOptions(event.query, _items);
            } else {
                let filterField = props.optionLabel ? props.optionLabel : 'label';
                filteredOptions = _items.filter((x) => {
                    if (x[filterField]) {
                        return x[filterField].toLowerCase().indexOf(event.query.toLowerCase()) !== -1;
                    }
                });

            }
            setFilteredOptions(filteredOptions);
        }

        return (
            <div className={props.caption ? 'field' : ''} key={props.keyName} style={{ width: '100%', ...props.style }}>
                {renderRequired()}
                {props.caption ? <><label style={props.labelStyle} className="item-authors" >{internalTranslate(props.caption)}</label><br></br></> : ''}

                <AutoComplete value={_autoCompleteValue} multiple={props.multiple} dropdown suggestions={_filteredOptions} completeMethod={searchOptions}
                    readOnly={props.readOnly} disabled={props.disabled} itemTemplate={props.itemTemplate} selectedItemTemplate={props.selectedTemplate}
                    onChange={handleDropChanged} onBlur={handleAutoBlur} id={props.id} field={props.optionLabel} className={`validation-dropdown ${_className ?? ''} ${props.editorClassName ?? ""}`} />
                <small ref={_dropdownHelpRef} id={`${props.id}-help`} style={{ display: 'none', marginLeft: 2 }} className="p-error p-d-block "></small>

            </div>
        )
    }





    return (
        <div className={props.className ?? ""} style={{ width: '100%', ...props.containerStyle }}>
            {
                props.type === 'dropdown' ? renderDropdown() : renderInputBox()
            }
        </div>
    )
});

